import { getWorldCupTeamBrandingForTeam } from '@shared/worldCupTeamBranding';
import { getTeamPrimaryColor, getTeamSecondaryColor } from './teamColors';
import { maybeGetTeamLogoUrl } from './teamLogos';

export interface TeamBrandingInput {
  abbreviation?: string | null;
  name?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
}

export interface TeamBrandingResult {
  logoUrl: string | null;
  backdropLogoTintColor: string | null;
  flagEmoji: string | null;
  flagImageUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  isWorldCupTeam: boolean;
}

const FIFA_TO_FLAGPEDIA_CODE_OVERRIDES: Record<string, string> = {
  ENG: 'gb-eng',
  SCO: 'gb-sct',
};

const BACKDROP_LOGO_TINT_COLORS: Record<string, string> = {
  LAR: '#FFA300',
  NYG: '#A71930',
  NYJ: '#FFFFFF',
};

const getIsoCountryCodeFromFlagEmoji = (flagEmoji?: string | null): string | null => {
  if (!flagEmoji) {
    return null;
  }

  const regionalIndicatorCodepoints = Array.from(flagEmoji).map((char) => char.codePointAt(0));
  const isCountryFlag =
    regionalIndicatorCodepoints.length === 2 &&
    regionalIndicatorCodepoints.every(
      (codepoint) => codepoint !== undefined && codepoint >= 0x1f1e6 && codepoint <= 0x1f1ff,
    );

  if (!isCountryFlag) {
    return null;
  }

  return regionalIndicatorCodepoints
    .map((codepoint) => String.fromCharCode((codepoint ?? 0) - 0x1f1e6 + 65))
    .join('')
    .toLowerCase();
};

const getFlagImageUrl = ({
  fifaCode,
  flagEmoji,
}: {
  fifaCode?: string | null;
  flagEmoji?: string | null;
}): string | null => {
  const normalizedFifaCode = fifaCode?.trim().toUpperCase();
  const flagpediaCode = (normalizedFifaCode && FIFA_TO_FLAGPEDIA_CODE_OVERRIDES[normalizedFifaCode]) ?? getIsoCountryCodeFromFlagEmoji(flagEmoji);

  if (!flagpediaCode) {
    return null;
  }

  return `https://flagcdn.com/w640/${flagpediaCode}.png`;
};

export function getTeamBranding(input: TeamBrandingInput): TeamBrandingResult {
  const logoUrl = maybeGetTeamLogoUrl(input.abbreviation);
  const normalizedAbbreviation = input.abbreviation?.trim().toUpperCase();
  const worldCupBranding = getWorldCupTeamBrandingForTeam({
    fifaCode: input.abbreviation,
    name: input.name,
  });

  return {
    logoUrl,
    // These ESPN marks use their teams' primary color and need contrast when
    // rendered as large watermarks on the corresponding color field.
    backdropLogoTintColor: (normalizedAbbreviation && BACKDROP_LOGO_TINT_COLORS[normalizedAbbreviation]) ?? null,
    flagEmoji: logoUrl ? null : worldCupBranding?.flagEmoji ?? null,
    flagImageUrl:
      logoUrl
        ? null
        : getFlagImageUrl({
            fifaCode: worldCupBranding?.fifaCode ?? input.abbreviation,
            flagEmoji: worldCupBranding?.flagEmoji,
          }),
    primaryColor:
      input.primaryColor ??
      worldCupBranding?.primaryColor ??
      getTeamPrimaryColor(input.abbreviation),
    secondaryColor:
      input.secondaryColor ??
      worldCupBranding?.secondaryColor ??
      getTeamSecondaryColor(input.abbreviation),
    isWorldCupTeam: !logoUrl && !!worldCupBranding,
  };
}
